import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button, Spinner, Table, Badge, Pagination, Alert, Form } from "react-bootstrap";
import api, { BASE_URL } from "../../Utils/api";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiDollarSign,
  FiExternalLink,
  FiFileText,
  FiRefreshCw,
} from "react-icons/fi";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { Cancelar } from "../Generals/buttons";
import formatarData from "../../Utils/formatarData";
import "./EmpresasFinanceiroModal.css";

const ITENS_POR_PAGINA = 10;
const ANO_ATUAL = new Date().getFullYear();

function formatarMoeda(valor) {
  if (valor == null || !Number.isFinite(Number(valor))) return "—";
  return Number(valor).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function statusPagamento(status, label) {
  const norm = String(status || "").toUpperCase();
  if (["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(norm)) {
    return { bg: "success", label: label || "Pago" };
  }
  if (norm === "PENDING") return { bg: "warning", label: label || "Pendente" };
  if (norm === "OVERDUE") return { bg: "danger", label: label || "Vencida" };
  if (["REFUNDED", "REFUND_REQUESTED"].includes(norm)) {
    return { bg: "info", label: label || "Estornada" };
  }
  if (["CANCELLED", "DELETED"].includes(norm)) {
    return { bg: "secondary", label: label || "Cancelada" };
  }
  return { bg: "secondary", label: label || status || "Não informado" };
}

function Indicador({ icon: Icon, label, value, hint, tone = "primary" }) {
  return (
    <div className="financeiro-kpi">
      <div className={`financeiro-kpi-icon financeiro-kpi-icon--${tone}`}>
        <Icon size={19} aria-hidden="true" />
      </div>
      <div className="financeiro-kpi-content">
        <span className="financeiro-kpi-label">{label}</span>
        <strong className="financeiro-kpi-value">{value}</strong>
        {hint && <span className="financeiro-kpi-hint">{hint}</span>}
      </div>
    </div>
  );
}

function PaginacaoTabela({ pagina, totalPaginas, totalRegistros, onChangePage }) {
  const inicio = totalRegistros === 0 ? 0 : (pagina - 1) * ITENS_POR_PAGINA + 1;
  const fim = Math.min(pagina * ITENS_POR_PAGINA, totalRegistros);

  return (
    <div className="financeiro-paginacao">
      <span>
        {totalRegistros > 0
          ? `Mostrando ${inicio}–${fim} de ${totalRegistros} registros`
          : "Nenhum registro"}
      </span>
      {totalPaginas > 1 && (
        <Pagination size="sm" className="mb-0">
          <Pagination.First onClick={() => onChangePage(1)} disabled={pagina === 1} />
          <Pagination.Prev onClick={() => onChangePage(pagina - 1)} disabled={pagina === 1} />
          {[...Array(totalPaginas)].map((_, index) => {
            const numero = index + 1;
            if (
              numero === 1 ||
              numero === totalPaginas ||
              (numero >= pagina - 1 && numero <= pagina + 1)
            ) {
              return (
                <Pagination.Item
                  key={numero}
                  active={numero === pagina}
                  onClick={() => onChangePage(numero)}
                >
                  {numero}
                </Pagination.Item>
              );
            }
            if (numero === 2 && pagina > 3) {
              return <Pagination.Ellipsis key="inicio" disabled />;
            }
            if (numero === totalPaginas - 1 && pagina < totalPaginas - 2) {
              return <Pagination.Ellipsis key="fim" disabled />;
            }
            return null;
          })}
          <Pagination.Next
            onClick={() => onChangePage(pagina + 1)}
            disabled={pagina === totalPaginas}
          />
          <Pagination.Last
            onClick={() => onChangePage(totalPaginas)}
            disabled={pagina === totalPaginas}
          />
        </Pagination>
      )}
    </div>
  );
}

function EmpresasFinanceiroModal({ show, handleClose, entidadeID, empresaNome, onAbrirPlanos }) {
  const { toast } = useFeedback();
  const [carregando, setCarregando] = useState(false);
  const [resumo, setResumo] = useState(null);
  // Cobranças reais (Asaas GET /payments) — distinto da visão de assinatura.
  const [cobrancas, setCobrancas] = useState([]);
  // Histórico de mudanças de plano (auditoria de eventos).
  const [historicoPlanos, setHistoricoPlanos] = useState([]);
  const [mostrarHistoricoPlanos, setMostrarHistoricoPlanos] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [anoSelecionado, setAnoSelecionado] = useState(ANO_ATUAL);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      id_entidade: entidadeID,
    }),
    [entidadeID]
  );

  const carregarFinanceiro = useCallback(async () => {
    if (!entidadeID) return;
    setCarregando(true);
    try {
      const { data } = await api.get(`${BASE_URL}/api/assinaturas/financeiro`, {
        headers: authHeaders,
      });
      const payload = data?.data || {};
      setResumo(payload.resumo || null);
      setCobrancas(Array.isArray(payload.cobrancas) ? payload.cobrancas : []);
      setHistoricoPlanos(
        Array.isArray(payload.historico_planos) ? payload.historico_planos : []
      );
      setPagina(1);
    } catch (err) {
      setResumo(null);
      setCobrancas([]);
      setHistoricoPlanos([]);
      setPagina(1);
      toast.error(err.response?.data?.message || "Erro ao carregar informações financeiras.");
    } finally {
      setCarregando(false);
    }
  }, [authHeaders, entidadeID, toast]);

  useEffect(() => {
    if (show) carregarFinanceiro();
    else {
      setResumo(null);
      setCobrancas([]);
      setHistoricoPlanos([]);
      setMostrarHistoricoPlanos(false);
      setPagina(1);
      setAnoSelecionado(ANO_ATUAL);
    }
  }, [show, carregarFinanceiro]);

  const anosDisponiveis = useMemo(() => {
    const anos = new Set([ANO_ATUAL]);
    cobrancas.forEach((item) => {
      if (!item.vencimento) return;
      const ano = Number(String(item.vencimento).slice(0, 4));
      if (Number.isInteger(ano)) anos.add(ano);
    });
    return [...anos].sort((a, b) => b - a);
  }, [cobrancas]);

  const cobrancasFiltradas = useMemo(
    () =>
      cobrancas.filter(
        (item) =>
          item.vencimento &&
          Number(String(item.vencimento).slice(0, 4)) === Number(anoSelecionado)
      ),
    [cobrancas, anoSelecionado]
  );

  const totalRegistros = cobrancasFiltradas.length;
  const totalPaginas = Math.ceil(totalRegistros / ITENS_POR_PAGINA) || 1;
  const cobrancasPagina = useMemo(() => {
    const inicio = (pagina - 1) * ITENS_POR_PAGINA;
    return cobrancasFiltradas.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [cobrancasFiltradas, pagina]);

  useEffect(() => {
    setPagina(1);
  }, [anoSelecionado]);

  useEffect(() => {
    if (pagina > totalPaginas) setPagina(totalPaginas);
  }, [pagina, totalPaginas]);

  const handlePagar = (cobranca) => {
    if (!cobranca?.invoice_url) {
      toast.warning("Link de pagamento indisponível para esta cobrança.");
      return;
    }
    window.open(cobranca.invoice_url, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      dialogClassName="modal-dialog-financeiro"
      contentClassName="financeiro-modal-content"
    >
      <Modal.Header closeButton className="financeiro-header">
        <div className="financeiro-header-main">
          <div className="financeiro-header-icon">
            <FiDollarSign size={22} aria-hidden="true" />
          </div>
          <div>
            <span className="financeiro-eyebrow">Gestão financeira</span>
            <Modal.Title>Financeiro</Modal.Title>
            <p>{empresaNome || "Entidade"}</p>
          </div>
        </div>
        <div
          className="financeiro-header-actions"
          style={{ display: "flex", gap: "var(--space-2, 8px)" }}
        >
          {typeof onAbrirPlanos === "function" && (
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="financeiro-header-refresh"
              onClick={onAbrirPlanos}
              disabled={carregando}
            >
              <FiCreditCard />
              Planos
            </Button>
          )}
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            className="financeiro-header-refresh"
            onClick={carregarFinanceiro}
            disabled={carregando}
          >
            <FiRefreshCw className={carregando ? "financeiro-spin" : ""} />
            Atualizar
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="modal-financeiro-body">
        {carregando && !resumo ? (
          <div className="modal-financeiro-loading" role="status">
            <Spinner animation="border" />
            <strong>Carregando financeiro</strong>
            <span>Consultando assinatura e cobranças no Asaas.</span>
          </div>
        ) : (
          <div className="modal-financeiro-conteudo">
            {resumo?.tem_integracao ? (
              <>
                <section className="financeiro-kpis" aria-label="Resumo financeiro">
                  <Indicador
                    icon={FiFileText}
                    label="Plano atual"
                    value={resumo.plano || "—"}
                    hint={resumo.ciclo ? `Cobrança ${resumo.ciclo.toLowerCase()}` : null}
                  />
                  <Indicador
                    icon={FiDollarSign}
                    label="Valor do plano"
                    value={formatarMoeda(resumo.valor)}
                    hint="Valor por ciclo"
                    tone="success"
                  />
                  <Indicador
                    icon={FiCalendar}
                    label="Próximo vencimento"
                    value={
                      resumo.proximo_vencimento
                        ? formatarData(resumo.proximo_vencimento)
                        : "—"
                    }
                    hint="Data prevista"
                    tone="info"
                  />
                  <Indicador
                    icon={resumo.tem_pendencia ? FiAlertCircle : FiCheckCircle}
                    label="Situação financeira"
                    value={resumo.tem_pendencia ? "Pagamento em aberto" : "Em dia"}
                    hint={resumo.tem_pendencia ? "Ação necessária" : "Nenhuma pendência"}
                    tone={resumo.tem_pendencia ? "warning" : "success"}
                  />
                </section>

                {resumo.tem_pendencia && (
                  <Alert variant="warning" className="financeiro-alerta">
                    <FiAlertCircle size={17} aria-hidden="true" />
                    <div>
                      <strong>Existe uma cobrança em aberto</strong>
                      <span>Use a ação “Pagar” no histórico abaixo para regularizar.</span>
                    </div>
                  </Alert>
                )}
              </>
            ) : (
              <Alert variant="info" className="financeiro-alerta">
                <FiCreditCard size={18} aria-hidden="true" />
                <div>
                  <strong>Nenhuma assinatura financeira encontrada</strong>
                  <span>
                    {resumo?.mensagem ||
                      "Escolha um plano nas configurações da entidade para iniciar."}
                  </span>
                </div>
              </Alert>
            )}

            <section className="financeiro-historico">
              <div className="financeiro-section-header">
                <div>
                  <span className="financeiro-eyebrow">Cobranças</span>
                  <h3>Histórico financeiro</h3>
                  <p>Cobranças geradas no Asaas — valor, forma de pagamento e situação.</p>
                </div>
                <div className="financeiro-section-filtros">
                  <Form.Group controlId="financeiro-ano" className="financeiro-filtro-ano">
                    <Form.Label>Ano</Form.Label>
                    <Form.Select
                      size="sm"
                      value={anoSelecionado}
                      onChange={(event) => setAnoSelecionado(Number(event.target.value))}
                      aria-label="Filtrar histórico por ano"
                    >
                      {anosDisponiveis.map((ano) => (
                        <option key={ano} value={ano}>{ano}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Badge bg="light" text="dark" pill>
                    {totalRegistros} {totalRegistros === 1 ? "registro" : "registros"}
                  </Badge>
                </div>
              </div>

              {totalRegistros > 0 ? (
                <>
                  <div className="modal-financeiro-tabela-wrap">
                    <Table hover responsive className="financeiro-tabela mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Vencimento</th>
                          <th>Plano</th>
                          <th>Valor</th>
                          <th>Ciclo</th>
                          <th>Forma</th>
                          <th>Status</th>
                          <th className="text-end">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cobrancasPagina.map((item) => {
                          const st = statusPagamento(item.status, item.status_label);
                          return (
                            <tr key={item.id}>
                              <td className="financeiro-data">
                                {item.vencimento ? formatarData(item.vencimento) : "—"}
                              </td>
                              <td><strong>{item.plano || "—"}</strong></td>
                              <td className="financeiro-valor">{formatarMoeda(item.valor)}</td>
                              <td>{item.ciclo || "—"}</td>
                              <td>{item.forma_pagamento_label || "—"}</td>
                              <td>
                                <Badge bg={st.bg} pill>{st.label}</Badge>
                              </td>
                              <td className="text-end">
                                {item.invoice_url ? (
                                  <Button
                                    variant={item.pode_pagar ? "primary" : "outline-secondary"}
                                    size="sm"
                                    className="financeiro-pagar"
                                    onClick={() => handlePagar(item)}
                                  >
                                    <FiExternalLink size={14} />
                                    {item.pode_pagar ? "Pagar" : "Ver fatura"}
                                  </Button>
                                ) : (
                                  <span className="financeiro-sem-acao">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  <PaginacaoTabela
                    pagina={pagina}
                    totalPaginas={totalPaginas}
                    totalRegistros={totalRegistros}
                    onChangePage={setPagina}
                  />
                </>
              ) : (
                <div className="financeiro-vazio">
                  <FiFileText size={28} aria-hidden="true" />
                  <strong>Nenhuma cobrança em {anoSelecionado}</strong>
                  <span>Selecione outro ano para consultar períodos anteriores.</span>
                </div>
              )}
            </section>

            {historicoPlanos.length > 0 && (
              <section className="financeiro-historico">
                <div className="financeiro-section-header">
                  <div>
                    <span className="financeiro-eyebrow">Assinatura</span>
                    <h3>Mudanças de plano</h3>
                    <p>Contratações e trocas de plano registradas para a entidade.</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setMostrarHistoricoPlanos((v) => !v)}
                  >
                    {mostrarHistoricoPlanos
                      ? "Ocultar histórico"
                      : `Ver histórico (${historicoPlanos.length})`}
                  </Button>
                </div>

                {mostrarHistoricoPlanos && (
                  <div className="modal-financeiro-tabela-wrap">
                    <Table hover responsive className="financeiro-tabela mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Plano</th>
                          <th>Ciclo</th>
                          <th>Origem</th>
                          <th>Responsável</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historicoPlanos.map((ev) => (
                          <tr key={ev.id}>
                            <td className="financeiro-data">
                              {ev.data ? formatarData(String(ev.data).slice(0, 10)) : "—"}
                            </td>
                            <td>
                              {ev.plano_anterior ? `${ev.plano_anterior} → ` : ""}
                              <strong>{ev.plano_novo || "—"}</strong>
                            </td>
                            <td>{ev.ciclo_novo || ev.ciclo_anterior || "—"}</td>
                            <td>{ev.origem || "—"}</td>
                            <td>{ev.usuario || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="modal-footer-padrao financeiro-footer">
        <div className="modal-footer-acoes-esquerda">
          <FiCreditCard size={15} aria-hidden="true" />
          <span>Pagamentos processados com segurança pelo Asaas.</span>
        </div>
        <div className="modal-footer-acoes-direita">
          <Cancelar onClick={handleClose} disabled={carregando} />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default EmpresasFinanceiroModal;
